import { Injectable, inject } from '@angular/core'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbLoaderService } from '@galabid/shared/utils/loader'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { gbCheckLoaded } from '@galabid/shared/utils/operators'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { GbDashboardPusherService } from 'libs/gb-dashboard/core/util/src/lib/gb-dashboard-pusher-service/gb-dashboard-pusher.service'
import { ProjectorContainersFacade } from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.facade'
import * as PresenterContainerActions from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.actions'
import { GbAddProjectorDialogComponent } from 'libs/gb-dashboard/projector/feature/src/lib/gb-add-projector-dialog/gb-add-projector-dialog.component'
import { GbAddSlideDialogComponent } from 'libs/gb-dashboard/projector/feature/src/lib/gb-add-slide-dialog/gb-add-slide-dialog.component'
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs'
import { PROJECTORS_LOADER } from './projectors.models'
import { ProjectorsFacade } from './projectors.facade'
import { ProjectorsApiService } from './projectors-api.service'
import * as ProjectorActions from './projectors.actions'

@Injectable()
export class ProjectorsEffects {
  private actions$ = inject(Actions)

  selectNextSlide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.selectNextSlide),
      withLatestFrom(
        this.projectorsFacade.allProjectors$,
        this.projectorsFacade.selectedProjectorId$,
        this.projectorContainersFacade.selectedProjectorContainer$,
      ),
      map(([, allProjectors, selectedProjectorId, selectedContainer]) => {
        const slides = allProjectors.filter((projector) => projector.container === selectedContainer?.uuid)
        const currentIndex = slides.map((slide) => slide.uuid).indexOf(selectedProjectorId || '')
        const nextSlide = slides[currentIndex + 1]

        return nextSlide ? ProjectorActions.selectProjector({ uuid: nextSlide.uuid }) : null
      }),
      filter((action): action is ReturnType<typeof ProjectorActions.selectProjector> => action !== null),
    ),
  )

  subscribeToPusherChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorActions.selectProjector),
        tap((action) => {
          this.gbDashboardPusherService.initProjectorPusher(action.uuid)
        }),
      ),
    { dispatch: false },
  )

  selectPreviousSlide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.selectPreviousSlide),
      withLatestFrom(
        this.projectorsFacade.allProjectors$,
        this.projectorsFacade.selectedProjectorId$,
        this.projectorContainersFacade.selectedProjectorContainer$,
      ),
      map(([, allProjectors, selectedProjectorId, selectedContainer]) => {
        const slides = allProjectors.filter((projector) => projector.container === selectedContainer?.uuid)
        const currentIndex = slides.map((slide) => slide.uuid).indexOf(selectedProjectorId || '')
        const previousSlide = slides[currentIndex - 1]

        return previousSlide ? ProjectorActions.selectProjector({ uuid: previousSlide.uuid }) : null
      }),
      filter((action): action is ReturnType<typeof ProjectorActions.selectProjector> => action !== null),
    ),
  )

  selectFirstSlide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.selectFirstSlide),
      withLatestFrom(this.projectorsFacade.allProjectors$, this.projectorContainersFacade.selectedProjectorContainer$),
      map(([, allProjectors, selectedContainer]) => {
        const slides = allProjectors.filter((projector) => projector.container === selectedContainer?.uuid)
        const firstSlide = slides[0]

        return firstSlide ? ProjectorActions.selectProjector({ uuid: firstSlide.uuid }) : null
      }),
      filter((action): action is ReturnType<typeof ProjectorActions.selectProjector> => action !== null),
    ),
  )

  loadProjectors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.loadProjectors),
      gbCheckLoaded(this.selectedAuctionFacade.urlName$),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) => {
        return this.projectorsApiService.getProjectors(urlName).pipe(
          map((projectors) => ProjectorActions.loadProjectorsSuccess({ projectors })),
          catchError((error) => of(ProjectorActions.loadProjectorsFailure({ error }))),
        )
      }),
    ),
  )

  reorderSlides$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.reorderSlides),
      tap(() =>
        this.gbLoaderService.showLoaderDialog(
          PROJECTORS_LOADER.reorderSlides.id,
          PROJECTORS_LOADER.reorderSlides.message,
        ),
      ),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      concatMap(([action, urlName]) => {
        return this.projectorsApiService.reorderSlides(urlName, action.slides).pipe(
          map((slides) => ProjectorActions.reorderSlidesSuccess({ slides })),
          catchError((error) => of(ProjectorActions.reorderSlidesFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(PROJECTORS_LOADER.reorderSlides.id)),
        )
      }),
    ),
  )

  addProjector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.addProjector),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.projectorsApiService.createProjector(action.name, urlName, action.templateUuid).pipe(
          map((projector) => {
            return ProjectorActions.addProjectorSuccess({ projector })
          }),
          catchError((error) => of(ProjectorActions.addProjectorFailure({ error }))),
        )
      }),
    ),
  )

  addSlide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.addSlide),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.projectorsApiService
          .createSlide(action.name, urlName, action.containerUuid, action.templateUuid)
          .pipe(
            map((projector) => {
              return ProjectorActions.addSlideSuccess({ projector, templateUuid: action.templateUuid })
            }),
            catchError((error) => of(ProjectorActions.addSlideFailure({ error }))),
          )
      }),
    ),
  )

  showCreateProjectorSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorActions.addProjectorSuccess),
        tap(() => this.gbNotificationService.success('PROJECTOR.CREATE_SUCCESS_MESSAGE')),
      ),
    { dispatch: false },
  )

  showCreateSlideSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorActions.addSlideSuccess),
        tap(() => this.gbNotificationService.success('PROJECTOR.CREATE_SLIDE_SUCCESS_MESSAGE')),
      ),
    { dispatch: false },
  )

  showUpdateProjectorSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorActions.updateProjectorSuccess),
        tap((action) => {
          if (action.showToast !== false) this.gbNotificationService.success('PROJECTOR.UPDATE_PROJECTOR_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  updateProjector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.updateProjector),
      tap(() => this.gbLoaderService.showLoaderDialog(PROJECTORS_LOADER.updateProjector.id)),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.projectorsApiService.updateProjector(action.projector, urlName).pipe(
          map((projector) =>
            ProjectorActions.updateProjectorSuccess({
              update: { id: projector.uuid, changes: projector },
            }),
          ),
          catchError((error) => of(ProjectorActions.updateProjectorFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(PROJECTORS_LOADER.updateProjector.id)),
        )
      }),
    ),
  )

  updateSelectedProjector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.updateSelectedProjector),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.projectorsFacade.selectedProjectorId$),
      switchMap(([action, urlName, selectedProjectorId]) => {
        return this.projectorsApiService
          .updateProjector({ ...action.projector, uuid: selectedProjectorId }, urlName)
          .pipe(
            mergeMap((projector) => {
              const actions: Action[] = [
                ProjectorActions.updateProjectorSuccess({
                  update: { id: projector.uuid, changes: projector },
                  showToast: false,
                }),
              ]
              return actions
            }),
            catchError((error) => of(ProjectorActions.updateProjectorFailure({ error }))),
          )
      }),
    ),
  )

  deleteProjector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.deleteProjector),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'PROJECTOR.CONFIRM_DELETE_TITLE_PROJECTOR',
            body: 'PROJECTOR.CONFIRM_DELETE_BODY_PROJECTOR',
            confirmButtonText: 'PROJECTOR.CONFIRM_DELETE_CONFIRM',
            cancelButtonText: 'PROJECTOR.CONFIRM_DELETE_CANCEL',
          },
          action,
        ),
      ),
      tap(() => {
        this.gbLoaderService.showLoaderDialog(
          PROJECTORS_LOADER.deleteProjector.id,
          PROJECTORS_LOADER.deleteProjector.message,
        )
      }),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.projectorsApiService.deleteProjector(action.uuid, urlName).pipe(
          map(() => ProjectorActions.deleteProjectorSuccess({ uuid: action.uuid })),
          catchError((error) => of(ProjectorActions.deleteProjectorFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(PROJECTORS_LOADER.deleteProjector.id)),
        )
      }),
    ),
  )

  deleteSlide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.deleteSlide),
      withLatestFrom(this.projectorsFacade.allProjectors$, this.projectorContainersFacade.selectedProjectorContainer$),
      map(([action, allProjectors, selectedContainer]) => {
        const slides = allProjectors.filter((projector) => projector.container === selectedContainer?.uuid)
        const currentIndex = slides.map((slide) => slide.uuid).indexOf(action.uuid)
        const fallbackSlide = slides[currentIndex - 1] || slides[currentIndex + 1]
        return { action, previousSlideUuid: fallbackSlide?.uuid }
      }),
      exhaustMap(({ action, previousSlideUuid }) =>
        this.gbDashboardDialogService
          .filterByConfirmDialog(
            {
              title: 'PRESENTER.DELETE_SLIDE.TITLE',
              body: 'PRESENTER.DELETE_SLIDE.BODY',
              confirmButtonText: 'PRESENTER.DELETE_SLIDE.CONFIRM',
              cancelButtonText: 'PRESENTER.DELETE_SLIDE.CANCEL',
            },
            action,
          )
          .pipe(map(() => ({ action, previousSlideUuid }))),
      ),
      tap(() => {
        this.gbLoaderService.showLoaderDialog(
          PROJECTORS_LOADER.deleteProjector.id,
          PROJECTORS_LOADER.deleteProjector.message,
        )
      }),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([{ action, previousSlideUuid }, urlName]) => {
        return this.projectorsApiService.deleteProjector(action.uuid, urlName).pipe(
          map(() =>
            ProjectorActions.deleteSlideSuccess({
              uuid: action.uuid,
              containerUuid: action.containerUuid,
              previousSlideUuid,
            }),
          ),
          catchError((error) => of(ProjectorActions.deleteProjectorFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(PROJECTORS_LOADER.deleteProjector.id)),
        )
      }),
    ),
  )

  switchToPreviousSlideAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.deleteSlideSuccess),
      map((action) => {
        return action.previousSlideUuid ? ProjectorActions.selectProjector({ uuid: action.previousSlideUuid }) : null
      }),
      filter((action): action is ReturnType<typeof ProjectorActions.selectProjector> => action !== null),
    ),
  )

  generatePresenterToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.generatePresenterToken),
      switchMap(() => {
        return this.projectorsApiService.getPresenterEditToken().pipe(
          map((response) =>
            ProjectorActions.generatePresenterTokenSuccess({
              otu_token: response.otu_token,
            }),
          ),
          catchError((error) => of(ProjectorActions.generatePresenterTokenFailure({ error }))),
        )
      }),
    ),
  )

  loadProjectorTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.loadProjectorTemplates),
      switchMap(() => {
        return this.projectorsApiService.getProjectorTemplates().pipe(
          map((templates) => ProjectorActions.loadProjectorTemplatesSuccess({ templates })),
          catchError((error) => of(ProjectorActions.loadProjectorTemplatesFailure({ error }))),
        )
      }),
    ),
  )

  deleteProjectorTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.deleteProjectorTemplate),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'PROJECTOR.CONFIRM_DELETE_TITLE',
            body: 'PROJECTOR.CONFIRM_DELETE_BODY',
            confirmButtonText: 'PROJECTOR.CONFIRM_DELETE_CONFIRM',
            cancelButtonText: 'PROJECTOR.CONFIRM_DELETE_CANCEL',
          },
          action,
        ),
      ),
      tap(() => {
        this.gbLoaderService.showLoaderDialog(
          PROJECTORS_LOADER.deleteProjectorTemplate.id,
          PROJECTORS_LOADER.deleteProjectorTemplate.message,
        )
      }),
      switchMap((action) => {
        return this.projectorsApiService.deleteProjectorTemplate(action.uuid).pipe(
          map(() =>
            ProjectorActions.deleteProjectorTemplateSuccess({
              uuid: action.uuid,
            }),
          ),
          catchError((error) => of(ProjectorActions.deleteProjectorTemplateFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(PROJECTORS_LOADER.deleteProjectorTemplate.id)),
        )
      }),
    ),
  )

  openAddEditProjectorDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorActions.openAddEditProjectorDialog),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbAddProjectorDialogComponent, {
            disableClose: true,
            minWidth: 450,
            maxWidth: 600,
          }),
        ),
      ),
    { dispatch: false },
  )

  openAddSlideDialog = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorActions.openAddSlideDialog),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbAddSlideDialogComponent, {
            disableClose: true,
            width: '700px',
            maxWidth: 1000,
          }),
        ),
      ),
    { dispatch: false },
  )

  openDuplicateSlideDialog = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorActions.openDuplicateSlideDialog),
        exhaustMap((action) =>
          this.gbDashboardDialogService.getDialogObs(GbAddSlideDialogComponent, {
            disableClose: true,
            minWidth: 450,
            maxWidth: 600,
            data: {
              projectorUuid: action.projectorUuid,
              mode: 'duplicate',
            },
          }),
        ),
      ),
    { dispatch: false },
  )

  closeAddEditProjectorDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorActions.addProjectorSuccess,
          ProjectorActions.addProjectorFailure,
          ProjectorActions.updateProjectorSuccess,
          ProjectorActions.updateProjectorFailure,
        ),
        map(() => this.gbDashboardDialogService.closeDialog(GbAddProjectorDialogComponent, true)),
      ),
    { dispatch: false },
  )

  closeAddSlideDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorActions.addSlideSuccess,
          ProjectorActions.addSlideFailure,
          PresenterContainerActions.duplicateSlideSuccess,
          PresenterContainerActions.duplicateSlideFailure,
        ),
        map(() => this.gbDashboardDialogService.closeDialog(GbAddSlideDialogComponent, true)),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorActions.addProjectorFailure,
          ProjectorActions.addSlideFailure,
          ProjectorActions.reorderSlidesFailure,
          ProjectorActions.updateProjectorFailure,
          ProjectorActions.deleteProjectorFailure,
          ProjectorActions.generatePresenterTokenFailure,
          ProjectorActions.loadProjectorsFailure,
          ProjectorActions.deleteProjectorTemplateFailure,
          ProjectorActions.updateProjectorTemplateFailure,
          ProjectorActions.loadProjectorTemplatesFailure,
        ),
        map((action) => {
          const errorMessage = this.gbErrorMessageService.getErrorMessage(action.error['error'])
          if (errorMessage && typeof errorMessage === 'string') this.gbNotificationService.error(errorMessage)
          else {
            this.gbNotificationService.error('PROJECTOR.GENERIC_ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  showSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorActions.deleteProjectorTemplateSuccess,
          ProjectorActions.deleteProjectorSuccess,
          ProjectorActions.deleteSlideSuccess,
        ),
        tap((action) => {
          let success_message = ''
          switch (action.type) {
            case ProjectorActions.deleteProjectorSuccess.type:
              success_message = 'PROJECTOR.DELETE_SUCCESS_MESSAGE_P'
              break
            case ProjectorActions.deleteProjectorTemplateSuccess.type:
              success_message = 'PROJECTOR.DELETE_SUCCESS_MESSAGE'
              break
            case ProjectorActions.deleteSlideSuccess.type:
              success_message = 'PRESENTER.DELETE_SLIDE_SUCCESS'
              break
          }
          this.gbNotificationService.success(success_message)
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private selectedAuctionFacade: SelectedAuctionFacade,
    private gbNotificationService: GbNotificationService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private gbLoaderService: GbLoaderService,
    private projectorsApiService: ProjectorsApiService,
    private projectorsFacade: ProjectorsFacade,
    private projectorContainersFacade: ProjectorContainersFacade,
    private gbDashboardPusherService: GbDashboardPusherService,
  ) {}
}
